import {
  AlwaysDepth,
  type DepthModes,
  DoubleSide,
  LessEqualDepth,
  ShaderMaterial,
} from "three";

/**
 * Vertex stage shared by the glow and by the depth passes that prepare its
 * group-local depth buffer. The glow is depth-tested with `LessEqual` against
 * depth the depth pass wrote from the same clone, so both programs have to
 * produce bit-identical positions; `invariant` is what GLSL guarantees that
 * with across separately compiled programs.
 */
export const GLOW_VERTEX_SHADER = /* glsl */ `
  invariant gl_Position;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vView = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const DEPTH_ONLY_FRAGMENT_SHADER = /* glsl */ `
  void main() {
    gl_FragColor = vec4(0.0);
  }
`;

function createDepthOnlyMaterial(depthFunc: DepthModes): ShaderMaterial {
  return new ShaderMaterial({
    vertexShader: GLOW_VERTEX_SHADER,
    fragmentShader: DEPTH_ONLY_FRAGMENT_SHADER,
    // Listed as transparent so it sorts into the same list as the glow and
    // `renderOrder` can interleave the two; nothing is blended, the colour
    // mask is off.
    transparent: true,
    // GL skips the depth write when the test is disabled, so the test stays
    // on and the function decides what gets written.
    depthTest: true,
    depthFunc,
    depthWrite: true,
    colorWrite: false,
    // Both faces, whatever the clone's own sidedness: for a closed mesh a back
    // face is never the nearest surface, and an open shell seen from behind
    // is invisible either way.
    side: DoubleSide,
    // Without this three draws a transparent double-sided material twice and
    // flips `side` on the shared material each time.
    forceSinglePass: true,
  });
}

interface XrayGlowPassMaterials {
  /** Pass 1: overwrite the scene's depth at the part with the part's own. */
  reset: ShaderMaterial;
  /** Pass 2: keep the nearest surface across the whole group. */
  depth: ShaderMaterial;
}

let cache: XrayGlowPassMaterials | null = null;

/**
 * The two colour-less depth materials every x-ray glow draws its clones with
 * before the tint. They carry no per-part state, so one pair serves the whole
 * canvas and is never disposed.
 *
 * Pass 1 writes the part's depth with the test forced to pass. Once every part
 * of the group has done so, each pixel inside the group's silhouette holds the
 * depth of *some* surface of the group instead of the case wall's. Pass 2 then
 * draws the same clones with `LessEqual`, which lowers each pixel to the
 * group's nearest surface. A far-plane reset is unnecessary for that: the
 * minimum of a set that already contains only group surfaces is the group's
 * minimum.
 */
export function getXrayGlowPassMaterials(): XrayGlowPassMaterials {
  cache ??= {
    reset: createDepthOnlyMaterial(AlwaysDepth),
    depth: createDepthOnlyMaterial(LessEqualDepth),
  };
  return cache;
}
